import React from 'react';
import { motion } from 'framer-motion';
import { formatTime, getStatusColor, getStatusLabel } from '../utils/formatters';

interface Course {
  _id: string;
  name: string;
  description: string;
  startDate: string;
  classDays: string[];
  timeSlots: any[];
  maxStudents: number;
  enrolledCount: number;
  level: any;
  format: string;
  status: string;
  price: number;
}

interface CourseListProps {
  courses: Course[];
  visibleCourses: number;
  newlyLoadedCourses: string[];
  isLoading: boolean;
}

const CourseList: React.FC<CourseListProps> = ({
  courses,
  visibleCourses,
  newlyLoadedCourses,
  isLoading,
}) => {
  const displayedCourses = courses.slice(0, visibleCourses);

  return (
    <motion.div
      className="space-y-4 mt-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {displayedCourses.map((course, index) => {
        const isNewlyLoaded = newlyLoadedCourses.includes(course._id);
        return (
          <motion.div
            key={course._id}
            initial={isNewlyLoaded ? { opacity: 0, y: 10 } : { opacity: 1 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 ${
              isLoading ? 'opacity-50' : ''
            }`}
          >
            {/* Course Name and Description */}
            <div className="mb-3">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {course.name}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                {course.description}
              </p>
            </div>

            {/* Level and Format */}
            <div className="flex gap-2 mb-3">
              <span className="text-xs bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 px-2 py-1 rounded">
                {course.level?.name || 'N/A'}
              </span>
              <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                {course.format === 'online' ? 'Trực tuyến' : 'Tại lớp'}
              </span>
            </div>

            {/* Course Details Grid */}
            <div className="grid grid-cols-2 gap-3 text-xs mb-3">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Khai giảng:</span>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {course.startDate}
                </div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Buổi/Ca:</span>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {course.classDays?.join(', ') || 'N/A'}
                </div>
                <div className="text-gray-500 dark:text-gray-500 text-xs">
                  {course.timeSlots?.[0]
                    ? `${formatTime(course.timeSlots[0].startTime)}`
                    : 'N/A'}
                </div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Sĩ số:</span>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {course.enrolledCount}/{course.maxStudents}
                </div>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Giá:</span>
                <div className="font-semibold text-[#017e6e] dark:text-emerald-400">
                  {course.price?.toLocaleString('vi-VN')}đ
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <span
                className={`text-xs px-2 py-1 rounded font-medium ${getStatusColor(
                  course.status
                )}`}
              >
                {getStatusLabel(course.status)}
              </span>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default CourseList;
